import React from "react";
import { Button } from "./ui/button";
import { RefreshCw, AlertTriangle, Home } from "lucide-react";
import { API_BASE } from "../lib/apiBase";
import { trHorsContexte as tr } from "../lib/langHorsContexte";

/**
 * React error boundary — catches render-time JS errors and shows a friendly
 * fallback instead of a blank screen. Logs to backend for monitoring.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null, errorId: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    const errorId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    this.setState({ error, errorInfo, errorId });

    // Best-effort report to backend (non-blocking)
    try {
      const url = API_BASE || "";
      const body = {
        message: error?.message || String(error),
        stack: error?.stack || "",
        component_stack: errorInfo?.componentStack || "",
        url: window.location.href,
        user_agent: navigator.userAgent,
        error_id: errorId,
        ts: new Date().toISOString(),
      };
      fetch(`${url}/api/client-errors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
        keepalive: true,
      }).catch(() => {});
    } catch (_) {
      // ignore
    }
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/dashboard";
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen flex items-center justify-center bg-[#0a4d3a] text-white px-6"
      >
        <div className="max-w-2xl w-full">
          <div className="flex items-start gap-4 mb-6">
            <AlertTriangle className="w-12 h-12 text-[#c89b3c] flex-shrink-0 mt-1" />
            <div>
              <h1 className="text-3xl font-serif mb-2">{tr("Une erreur s'est produite", "Something went wrong")}</h1>
              <p className="text-white/80">
                {tr(
                  "Knoria a rencontré un problème inattendu. Notre équipe a été notifiée.",
                  "Knoria hit an unexpected problem. Our team has been notified.",
                )}
              </p>
            </div>
          </div>

          <div className="bg-black/30 border border-white/10 rounded-lg p-4 mb-6 font-mono text-sm">
            <div className="text-white/60 mb-1">{tr("ID d'erreur", "Error ID")}</div>
            <div data-testid="error-boundary-id" className="text-[#c89b3c] mb-3">{this.state.errorId}</div>
            <div className="text-white/60 mb-1">{tr("Message", "Message")}</div>
            <div className="text-white/90 break-words">
              {this.state.error?.message || String(this.state.error)}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="error-boundary-reload-btn"
              onClick={this.handleReload}
              className="bg-[#c89b3c] hover:bg-[#b8893c] text-[#0a4d3a]"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {tr("Recharger", "Reload")}
            </Button>
            <Button
              data-testid="error-boundary-home-btn"
              onClick={this.handleHome}
              variant="outline"
              className="bg-transparent border-white/30 text-white hover:bg-white/10"
            >
              <Home className="w-4 h-4 mr-2" />
              {tr("Tableau de bord", "Dashboard")}
            </Button>
            <a
              href={`mailto:support@knoria.com?subject=Erreur%20${encodeURIComponent(this.state.errorId)}`}
              className="inline-flex"
            >
              <Button
                variant="outline"
                className="bg-transparent border-white/30 text-white hover:bg-white/10"
              >
                {tr("Contacter le support", "Contact support")}
              </Button>
            </a>
          </div>

          <p className="text-white/50 text-sm mt-6">
            {tr(
              "Mentionnez l'identifiant ci-dessus si vous écrivez au support — ça nous aidera à retrouver le problème exact.",
              "Quote the ID above if you write to support — it helps us find the exact problem.",
            )}
          </p>
        </div>
      </div>
    );
  }
}
